import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import PublicIcon from "@mui/icons-material/Public";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import ViewListOutlinedIcon from "@mui/icons-material/ViewListOutlined";
import PercentIcon from "@mui/icons-material/Percent";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AdsClickIcon from "@mui/icons-material/AdsClick";
import BarChartIcon from "@mui/icons-material/BarChart";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import MemoryOutlinedIcon from "@mui/icons-material/MemoryOutlined";
import PriceChangeOutlinedIcon from "@mui/icons-material/PriceChangeOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { components } from "../../types/api";
import {
  useCalculateGoalFinancialsMutation,
  useCreateAssociatedCampaignGoalMutation,
  useUpdateAssociatedCampaignGoalMutation,
} from "../../store/apis/briefingApi";
import { useGetCurrenciesQuery } from "../../store/apis/currencyApi";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { useUserRole } from "../../hooks/useUserRole";
import {
  canEditGoalField,
  canViewGoalField,
} from "../../utils/briefingPermissions";
import { getCountryLabel } from "../../utils/countryUtils";
import { plainNumber } from "../../utils/format";
import { CountrySelect } from "../common/CountrySelect";
import ConfirmationModal from "../modals/ConfirmationModal";
import { GoalFormState } from "./GoalDialog";
import SectionCard from "./inline/SectionCard";
import FieldGroup from "./inline/FieldGroup";
import InlineField from "./inline/InlineField";
import {
  InlineNumber,
  InlineSelect,
  InlineText,
  SectionEmpty,
} from "./inline/inputs";
import {
  bindField,
  FieldErrors,
  useInlineSection,
} from "./inline/useInlineSection";
import { extractApiErrorMessage } from "./inline/apiErrors";
import { createNumberFormat } from "../../utils/numberFormat";

type Goal = components["schemas"]["GoalResponse"];
type BriefingPlatform = components["schemas"]["BriefingPlatform"];
type BriefingStatus = components["schemas"]["BriefingStatus"];
type BriefingObjective = GoalFormState["objective"];
type BriefingFunnel = GoalFormState["funnel"];
type GoalKey = keyof GoalFormState;

const OBJECTIVES: BriefingObjective[] = [
  "CPC",
  "CPM",
  "CPMV",
  "CPV",
  "CPE",
  "CPA",
  "CPL",
];
const FUNNELS: BriefingFunnel[] = ["AWARENESS", "CONSIDERATION", "PERFORMANCE"];

type AdserverFormat = "VIDEO" | "RICH_MEDIA_NO_VIDEO" | "BANNER";
const ADSERVER_COST_BY_FORMAT: Record<AdserverFormat, number> = {
  VIDEO: 0.25,
  RICH_MEDIA_NO_VIDEO: 0.15,
  BANNER: 0.04,
};
const deriveAdserverFormat = (cost: number): AdserverFormat => {
  if (Math.abs(cost - ADSERVER_COST_BY_FORMAT.BANNER) < 0.01) return "BANNER";
  if (Math.abs(cost - ADSERVER_COST_BY_FORMAT.VIDEO) < 0.01) return "VIDEO";
  return "RICH_MEDIA_NO_VIDEO";
};

const DV360_AUTOTECH_PLATFORMS: BriefingPlatform[] = [
  "BEAPOLO_NETFLIX",
  "BEAPOLO_SAMSUNG_TV",
  "BEAPOLO_DISNEY_PLUS",
  "BEAPOLO_MOVISTAR",
];

/** Decimals each numeric field is stored/edited with. */
const DECIMALS: Partial<Record<GoalKey, number>> = {
  margin: 2,
  expectedVolume: 6,
  visibility: 2,
  ctr: 2,
  netUnitCost: 4,
  zeotapDataCost: 4,
  adserverCost: 4,
  impressionTrackerCost: 4,
  clickTrackerCost: 4,
  adtechCost: 4,
  grossUnitCost: 4,
  platformInversion: 2,
  totalInversion: 2,
};
const NUMERIC_FIELDS = Object.keys(DECIMALS) as GoalKey[];
const REQUIRED_TEXT_FIELDS: GoalKey[] = [
  "campaign",
  "country",
  "campaignConcept",
  "orderName",
  "lineName",
];
const PERCENT_FIELDS: GoalKey[] = ["margin", "visibility", "ctr"];
const MONEY_FIELDS: GoalKey[] = [
  "netUnitCost",
  "zeotapDataCost",
  "adserverCost",
  "impressionTrackerCost",
  "clickTrackerCost",
  "adtechCost",
  "grossUnitCost",
  "platformInversion",
  "totalInversion",
];

/** Values that feed the financial calculation. */
const FINANCIAL_INPUTS: GoalKey[] = [
  "grossUnitCost",
  "margin",
  "totalInversion",
  "objective",
  "impressionTrackerCost",
  "clickTrackerCost",
  "zeotapDataCost",
  "ctr",
  "visibility",
  "adserverCost",
  "adtechCost",
  "netUnitCost",
];

/** Calculated (blue) fields and how they are calculated. */
const AUTO_FIELDS: Partial<Record<GoalKey, string>> = {
  campaign: "Company_Concept_Country_Funnel",
  campaignConcept: "Campaign_Product_Subcampaign",
  orderName: "Company_Concept_Country_Funnel_Type",
  lineName: "Company_Concept_Country_Funnel_Type_Objective",
  expectedVolume: "TotalInversion / GrossUnitCost (CPM × 1000)",
  netUnitCost:
    "GrossUnitCost × (1 − Margin/100) − ClickTracker − ImprTracker − AdServer − Zeotap − Adtech",
  platformInversion: "ExpectedVolume × NetUnitCost (CPM ÷ 1000)",
};

const EMPTY_FORM: GoalFormState = {
  startDate: "",
  endDate: "",
  currency: "",
  margin: "",
  campaign: "",
  country: "",
  objective: "CPC",
  funnel: "AWARENESS",
  campaignConcept: "",
  orderName: "",
  lineName: "",
  expectedVolume: "",
  visibility: "",
  ctr: "",
  netUnitCost: "",
  zeotapDataCost: "",
  adserverCost: "",
  impressionTrackerCost: "",
  clickTrackerCost: "",
  adtechCost: "",
  grossUnitCost: "",
  platformInversion: "",
  totalInversion: "",
  status: "",
  notes: "",
};

const toFixedOrEmpty = (value: unknown, decimals: number) =>
  value === null || value === undefined || value === ""
    ? ""
    : Number(value).toFixed(decimals);

const goalToForm = (goal: Goal): GoalFormState => {
  const raw = goal as unknown as Record<string, unknown>;
  const form: GoalFormState = { ...EMPTY_FORM };
  (Object.keys(EMPTY_FORM) as GoalKey[]).forEach((key) => {
    const value = raw[key];
    if (DECIMALS[key] !== undefined) {
      (form as Record<GoalKey, string>)[key] = toFixedOrEmpty(
        value,
        DECIMALS[key] as number
      );
    } else if (value !== null && value !== undefined) {
      (form as Record<GoalKey, string>)[key] = String(value);
    }
  });
  form.startDate = goal.startDate ? goal.startDate.split("T")[0] : "";
  form.endDate = goal.endDate ? goal.endDate.split("T")[0] : "";
  return form;
};

const goalFieldsEqual = (key: GoalKey, a: unknown, b: unknown) => {
  if (a === b) return true;
  if (NUMERIC_FIELDS.includes(key)) {
    const sa = String(a ?? "").trim();
    const sb = String(b ?? "").trim();
    if (sa === "" || sb === "") return sa === sb;
    return Number(sa) === Number(sb);
  }
  return String(a ?? "") === String(b ?? "");
};

interface AssociatedCampaignGoalSectionProps {
  briefingId: string;
  associatedId: string;
  goal: Goal | null;
  /** Section-level permission; each field also checks the role rules. */
  canEdit: boolean;
  onDelete?: () => void;
  /** Reloads the campaign; resolves once fresh data is in. */
  onSaved: () => Promise<unknown>;
  associatedCampaignName?: string;
  companyName: string;
  campaignName: string;
  brandOrProduct: string;
  subcampaignName?: string | null;
  isBeApoloCompany: boolean;
  platformType?: BriefingPlatform;
  campaignType?: string | null;
  briefingStatus?: BriefingStatus;
  briefingTotalBudget?: number;
}

const AssociatedCampaignGoalSection: React.FC<
  AssociatedCampaignGoalSectionProps
> = ({
  briefingId,
  associatedId,
  goal,
  canEdit,
  onDelete,
  onSaved,
  associatedCampaignName,
  companyName,
  campaignName,
  brandOrProduct,
  subcampaignName,
  isBeApoloCompany,
  platformType,
  campaignType,
  briefingStatus,
  briefingTotalBudget,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { role } = useUserRole();

  const [createGoal] = useCreateAssociatedCampaignGoalMutation();
  const [updateGoal] = useUpdateAssociatedCampaignGoalMutation();
  const [calculateFinancials] = useCalculateGoalFinancialsMutation();

  const [creating, setCreating] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [adserverManual, setAdserverManual] = useState(false);
  const [adtechManual, setAdtechManual] = useState(false);
  const [budgetConfirm, setBudgetConfirm] = useState<
    ((ok: boolean) => void) | null
  >(null);

  const canView = useCallback(
    (key: GoalKey) => (role ? canViewGoalField(role, key) : false),
    [role]
  );
  const canEditField = useCallback(
    (key: GoalKey) =>
      canEdit &&
      !AUTO_FIELDS[key] &&
      (role ? canEditGoalField(role, key, briefingStatus) : false),
    [canEdit, role, briefingStatus]
  );

  // --- derived names (same rules as the goal form) ---
  const primeTag = platformType === "BEAPOLO_AMAZON_PRIME" ? "PRIME" : "";
  const computeNames = useCallback(
    (values: GoalFormState): Partial<GoalFormState> => {
      const join = (parts: Array<string | null | undefined>) =>
        parts.filter((p) => Boolean(p) && p !== "UNDEFINED").join("_");
      const concept = [campaignName, brandOrProduct, subcampaignName]
        .map((p) => p?.trim())
        .filter(Boolean)
        .join("_");
      const head = [
        companyName,
        campaignName?.trim(),
        brandOrProduct?.trim(),
        subcampaignName?.trim(),
        values.country.trim(),
        values.funnel,
      ];
      if (!companyName) return concept ? { campaignConcept: concept } : {};
      return {
        ...(concept && { campaignConcept: concept }),
        campaign: join(head),
        orderName: join([...head, campaignType, primeTag]),
        lineName: join([...head, campaignType, primeTag, values.objective]),
      };
    },
    [
      companyName,
      campaignName,
      brandOrProduct,
      subcampaignName,
      campaignType,
      primeTag,
    ]
  );

  // Defaults for a new goal: funnel (and its usual KPI) from the line name.
  const createDefaults = useMemo<GoalFormState>(() => {
    const funnel = associatedCampaignName?.match(
      /(AWARENESS|CONSIDERATION|PERFORMANCE)/
    )?.[1] as BriefingFunnel | undefined;
    const objective: BriefingObjective | undefined =
      funnel === "AWARENESS"
        ? "CPM"
        : funnel === "CONSIDERATION"
          ? "CPC"
          : funnel === "PERFORMANCE"
            ? "CPA"
            : undefined;
    const isDirectos = platformType === "BEAPOLO";
    const values: GoalFormState = {
      ...EMPTY_FORM,
      ...(funnel && { funnel }),
      ...(objective && { objective }),
      adserverCost: isBeApoloCompany
        ? ADSERVER_COST_BY_FORMAT.RICH_MEDIA_NO_VIDEO.toFixed(2)
        : "",
      ctr: isDirectos ? "0.30" : "",
      visibility: isDirectos ? "80" : "",
    };
    return { ...values, ...computeNames(values) };
  }, [associatedCampaignName, platformType, isBeApoloCompany, computeNames]);

  const base = useMemo<GoalFormState>(
    () => (creating || !goal ? createDefaults : goalToForm(goal)),
    [creating, goal, createDefaults]
  );

  // --- validation + save ---
  const validate = (draft: GoalFormState): FieldErrors<GoalFormState> => {
    const errors: FieldErrors<GoalFormState> = {};
    const required = t("validation.required", "This field is required");
    const checkable = (key: GoalKey) =>
      canView(key) &&
      (role ? canEditGoalField(role, key, briefingStatus) : true);

    REQUIRED_TEXT_FIELDS.forEach((key) => {
      if (checkable(key) && !draft[key].trim()) errors[key] = required;
    });
    NUMERIC_FIELDS.forEach((key) => {
      if (!checkable(key)) return;
      const value = draft[key];
      if (!value || !value.trim()) errors[key] = required;
      else if (Number.isNaN(Number(value)))
        errors[key] = t("validation.invalidNumber", "Enter a valid number");
    });
    (["startDate", "endDate"] as GoalKey[]).forEach((key) => {
      if (!checkable(key)) return;
      if (!draft[key]) errors[key] = required;
      else if (Number.isNaN(new Date(draft[key]).getTime()))
        errors[key] = t("validation.invalidDate", "Invalid date");
    });
    if (
      !errors.startDate &&
      !errors.endDate &&
      checkable("endDate") &&
      draft.startDate &&
      draft.endDate &&
      new Date(draft.endDate) < new Date(draft.startDate)
    ) {
      errors.endDate = t(
        "validation.endBeforeStart",
        "End date cannot be before start date"
      );
    }
    return errors;
  };

  const buildPayload = (draft: GoalFormState) => {
    const raw: Record<string, unknown> = {
      startDate: draft.startDate
        ? new Date(draft.startDate).toISOString()
        : undefined,
      endDate: draft.endDate
        ? new Date(draft.endDate).toISOString()
        : undefined,
      currency: draft.currency?.trim() || undefined,
      campaign: draft.campaign?.trim() || "",
      country: draft.country?.trim() || "",
      objective: draft.objective,
      funnel: draft.funnel,
      campaignConcept: draft.campaignConcept?.trim() || "",
      orderName: draft.orderName?.trim() || "",
      lineName: draft.lineName?.trim() || "",
      status: draft.status?.trim() || null,
      notes: draft.notes?.trim() || null,
    };
    NUMERIC_FIELDS.forEach((key) => {
      raw[key] = Number(draft[key]) || 0;
    });
    if (role) {
      Object.keys(raw).forEach((key) => {
        if (!canEditGoalField(role, key, briefingStatus)) delete raw[key];
      });
    }
    return raw as Parameters<typeof createGoal>[0]["body"];
  };

  const askBudgetConfirmation = () =>
    new Promise<boolean>((resolve) => setBudgetConfirm(() => resolve));

  const handleSave = async (draft: GoalFormState) => {
    const errors = validate(draft);
    if (Object.keys(errors).length > 0) return errors;

    if (
      briefingTotalBudget !== undefined &&
      Number(draft.totalInversion) > briefingTotalBudget &&
      !(await askBudgetConfirmation())
    ) {
      return false;
    }

    const isCreate = creating || !goal;
    const params = { briefingId, associatedId };
    try {
      if (isCreate) {
        await createGoal({ params, body: buildPayload(draft) }).unwrap();
      } else {
        await updateGoal({ params, body: buildPayload(draft) }).unwrap();
      }
      await onSaved();
      setCreating(false);
      setAdserverManual(false);
      setAdtechManual(false);
      dispatch(
        setToastMessage({
          message: isCreate
            ? t("briefingsView.goalCreateSuccess", "Goal created successfully")
            : t("briefingsView.goalUpdateSuccess", "Goal updated successfully"),
          severity: "success",
        })
      );
      return true;
    } catch (error) {
      dispatch(
        setToastMessage({
          message:
            extractApiErrorMessage(error) ??
            (isCreate
              ? t("briefingsView.goalCreateError", "Failed to create goal")
              : t("briefingsView.goalUpdateError", "Failed to update goal")),
          severity: "error",
        })
      );
      return false;
    }
  };

  const section = useInlineSection<GoalFormState>({
    id: "goal",
    base,
    onSave: handleSave,
    isEqual: goalFieldsEqual as (
      key: GoalKey,
      a: unknown,
      b: unknown
    ) => boolean,
    forceEdit: creating,
    readOnly: !canEdit || (!goal && !creating),
  });
  const { draft, setFields } = section;

  const discard = () => {
    section.discard();
    setCreating(false);
    setAdserverManual(false);
    setAdtechManual(false);
  };

  /** Changes a field the derived names depend on. */
  const setNameSource = (patch: Partial<GoalFormState>) =>
    setFields({ ...patch, ...computeNames({ ...draft, ...patch }) });

  const revertNameSource = (key: GoalKey) => () => {
    const next = { ...draft, [key]: base[key] };
    section.cancelField(key);
    setFields(computeNames(next));
  };

  // --- financial recalculation, only once a financial input changed ---
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const financialSignature = FINANCIAL_INPUTS.map((key) => draft[key]).join(
    "|"
  );
  const financialDirty = FINANCIAL_INPUTS.some((key) => section.isDirty(key));
  const shouldCalculate = creating || financialDirty;

  useEffect(() => {
    if (!shouldCalculate) return;
    const canSeeMargin = role ? canViewGoalField(role, "margin") : false;
    const handler = setTimeout(async () => {
      const values = draftRef.current;
      const num = (key: GoalKey) => parseFloat(values[key]) || 0;
      setIsCalculating(true);
      try {
        const res = await calculateFinancials({
          platform: platformType ?? "",
          campaignType: campaignType ?? "",
          objective: values.objective,
          grossUnitCost: num("grossUnitCost"),
          margin: num("margin"),
          totalInversion: num("totalInversion"),
          visibility: num("visibility"),
          ctr: num("ctr"),
          netUnitCost: num("netUnitCost"),
          adserverCost: num("adserverCost"),
          adtechCost: num("adtechCost"),
          impressionTrackerCost: num("impressionTrackerCost"),
          clickTrackerCost: num("clickTrackerCost"),
          zeotapDataCost: num("zeotapDataCost"),
          autoAdserverCost:
            !adserverManual && (role === "ADMIN" || role === "PRICING"),
          autoAdtechCost: !adtechManual,
        }).unwrap();

        const updates: Partial<GoalFormState> = {};
        const put = (key: GoalKey, value: number | undefined) => {
          if (value === undefined || value === null) return;
          const formatted = Number(value).toFixed(DECIMALS[key] ?? 2);
          if (!goalFieldsEqual(key, formatted, values[key])) {
            (updates as Record<GoalKey, string>)[key] = formatted;
          }
        };
        put("visibility", res.visibility);
        put("ctr", res.ctr);
        if (!adserverManual) put("adserverCost", res.adserverCost);
        if (!adtechManual) put("adtechCost", res.adtechCost);
        put("expectedVolume", res.expectedVolume);
        put("platformInversion", res.platformInversion);
        if (canSeeMargin) put("netUnitCost", res.netUnitCost);
        if (Object.keys(updates).length > 0) setFields(updates);
      } catch (err) {
        console.error("Failed to calculate financials", err);
      } finally {
        setIsCalculating(false);
      }
    }, 400);
    return () => clearTimeout(handler);
  }, [
    shouldCalculate,
    financialSignature,
    adserverManual,
    adtechManual,
    platformType,
    campaignType,
    role,
    calculateFinancials,
    setFields,
  ]);

  // --- currencies ---
  const { data: currencies, isLoading: isLoadingCurrencies } =
    useGetCurrenciesQuery(undefined, {
      skip: !(creating || section.isEditing("currency")),
    });
  const currencyOptions = (currencies ?? []).map((c) => ({
    value: c.code,
    label: `${c.symbol} ${c.name}`,
  }));

  const adtechPercentage =
    platformType === "BEAPOLO_AMAZON_PRIME" || platformType === "BEAPOLO_TWITCH"
      ? 0.05
      : platformType && DV360_AUTOTECH_PLATFORMS.includes(platformType)
        ? 0.15
        : null;

  // --- display helpers ---
  const currency = draft.currency || goal?.currency || "";
  const formatValue = (key: GoalKey, value: string): string => {
    if (NUMERIC_FIELDS.includes(key)) {
      const n = Number(value);
      if (value === "" || Number.isNaN(n)) return value;
      const precision =
        key === "expectedVolume"
          ? 0
          : key.toLowerCase().includes("cost")
            ? 4
            : 2;
      if (MONEY_FIELDS.includes(key) && currency) {
        try {
          return createNumberFormat({
            style: "currency",
            currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: precision,
          }).format(n);
        } catch {
          // Unknown currency code: plain number + code.
        }
      }
      const formatted = createNumberFormat({
        minimumFractionDigits: 0,
        maximumFractionDigits: precision,
      }).format(n);
      if (PERCENT_FIELDS.includes(key)) return `${formatted} %`;
      return MONEY_FIELDS.includes(key) && currency
        ? `${formatted} ${currency}`
        : formatted;
    }
    if (key === "startDate" || key === "endDate") {
      return value ? dayjs(value).format("DD/MM/YYYY") : "";
    }
    if (key === "country") return getCountryLabel(value);
    return value;
  };
  const copyValueOf = (key: GoalKey, value: string) => {
    if (!value) return null;
    if (NUMERIC_FIELDS.includes(key) && !Number.isNaN(Number(value))) {
      return plainNumber(
        Number(value),
        key === "expectedVolume"
          ? 0
          : key.toLowerCase().includes("cost")
            ? 4
            : 2
      );
    }
    return formatValue(key, value);
  };

  const ICONS: Record<GoalKey, React.ReactElement> = {
    startDate: <EventOutlinedIcon />,
    endDate: <EventOutlinedIcon />,
    objective: <TrackChangesIcon />,
    funnel: <FilterAltOutlinedIcon />,
    country: <PublicIcon />,
    campaign: <CampaignOutlinedIcon />,
    campaignConcept: <LightbulbOutlinedIcon />,
    orderName: <ReceiptLongOutlinedIcon />,
    lineName: <ViewListOutlinedIcon />,
    margin: <PercentIcon />,
    visibility: <VisibilityOutlinedIcon />,
    ctr: <AdsClickIcon />,
    expectedVolume: <BarChartIcon />,
    netUnitCost: <SellOutlinedIcon />,
    zeotapDataCost: <StorageOutlinedIcon />,
    adserverCost: <DnsOutlinedIcon />,
    impressionTrackerCost: <RemoveRedEyeOutlinedIcon />,
    clickTrackerCost: <TouchAppOutlinedIcon />,
    adtechCost: <MemoryOutlinedIcon />,
    grossUnitCost: <PriceChangeOutlinedIcon />,
    totalInversion: <AccountBalanceWalletOutlinedIcon />,
    platformInversion: <StoreOutlinedIcon />,
    currency: <CurrencyExchangeIcon />,
    status: <FactCheckOutlinedIcon />,
    notes: <NotesOutlinedIcon />,
  };

  const LABELS: Record<GoalKey, string> = {
    startDate: t("briefingsView.goalForm.startDate", "Start date"),
    endDate: t("briefingsView.goalForm.endDate", "End date"),
    objective: t("briefingsView.goalForm.objective", "Objective"),
    funnel: t("briefingsView.goalForm.funnel", "Funnel"),
    country: t("briefingsView.goalForm.country", "Country"),
    campaign: t("briefingsView.goalForm.campaign", "Campaign"),
    campaignConcept: t(
      "briefingsView.goalForm.campaignConcept",
      "Campaign concept"
    ),
    orderName: t("briefingsView.goalForm.orderName", "Order name"),
    lineName: t("briefingsView.goalForm.lineName", "Line name"),
    margin: t("briefingsView.goalForm.margin", "Margin"),
    visibility: t("briefingsView.goalForm.visibility", "Visibility"),
    ctr: t("briefingsView.goalForm.ctr", "CTR"),
    expectedVolume: t(
      "briefingsView.goalForm.expectedVolume",
      "Expected volume"
    ),
    netUnitCost: t("briefingsView.goalForm.netUnitCost", "Net unit cost"),
    zeotapDataCost: t(
      "briefingsView.goalForm.zeotapDataCost",
      "Zeotap data cost"
    ),
    adserverCost: t("briefingsView.goalForm.adserverCost", "Adserver cost"),
    impressionTrackerCost: t(
      "briefingsView.goalForm.impressionTrackerCost",
      "Impression tracker cost"
    ),
    clickTrackerCost: t(
      "briefingsView.goalForm.clickTrackerCost",
      "Click tracker cost"
    ),
    adtechCost: t("briefingsView.goalForm.adtechCost", "Adtech cost"),
    grossUnitCost: t("briefingsView.goalForm.grossUnitCost", "Gross unit cost"),
    totalInversion: t(
      "briefingsView.goalForm.totalInversion",
      "Total inversion"
    ),
    platformInversion: t(
      "briefingsView.goalForm.platformInversion",
      "Platform inversion"
    ),
    currency: t("briefingsView.goalForm.currency", "Currency"),
    status: t("briefingsView.goalForm.status", "Status"),
    notes: t("briefingsView.goalForm.notes", "Notes"),
  };

  const changeNumber = (key: GoalKey, value: string) => {
    if (key === "adserverCost") setAdserverManual(true);
    if (key === "adtechCost") setAdtechManual(true);
    section.setField(key, value);
  };

  const renderInput = (key: GoalKey): React.ReactNode => {
    const error = Boolean(section.errors[key]);
    const autoFocus = section.isSoloEditing(key);
    switch (key) {
      case "startDate":
      case "endDate":
        return (
          <InlineText
            type="date"
            value={draft[key]}
            onChange={(event) => section.setField(key, event.target.value)}
            error={error}
            autoFocus={autoFocus}
          />
        );
      case "objective":
        return (
          <InlineSelect
            value={draft.objective}
            options={OBJECTIVES.map((o) => ({ value: o, label: o }))}
            onChange={(value) =>
              setNameSource({ objective: value as BriefingObjective })
            }
          />
        );
      case "funnel":
        return (
          <InlineSelect
            value={draft.funnel}
            options={FUNNELS.map((f) => ({ value: f, label: f }))}
            onChange={(value) =>
              setNameSource({ funnel: value as BriefingFunnel })
            }
          />
        );
      case "country":
        return (
          <CountrySelect
            label=""
            value={draft.country}
            onChange={(value) => setNameSource({ country: value })}
            error={error}
          />
        );
      case "currency":
        return (
          <InlineSelect
            value={draft.currency}
            options={
              currencyOptions.length > 0
                ? currencyOptions
                : draft.currency
                  ? [{ value: draft.currency, label: draft.currency }]
                  : []
            }
            emptyLabel={
              isLoadingCurrencies ? t("common.loading", "Loading…") : undefined
            }
            onChange={(value) => section.setField("currency", value)}
          />
        );
      case "notes":
        return (
          <InlineText
            multiline
            minRows={3}
            value={draft.notes}
            onChange={(event) => section.setField("notes", event.target.value)}
            autoFocus={autoFocus}
          />
        );
      case "status":
        return (
          <InlineText
            value={draft.status}
            onChange={(event) => section.setField("status", event.target.value)}
            autoFocus={autoFocus}
          />
        );
      case "adserverCost":
        return (
          <Stack spacing={1}>
            {isBeApoloCompany && (
              <>
                <Typography variant="caption" color="text.secondary">
                  {t(
                    "briefingsCreate.form.hasVideoHelper",
                    "Selecting an option pre-fills the Adserver fee, but you can edit it."
                  )}
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  color="primary"
                  value={deriveAdserverFormat(Number(draft.adserverCost) || 0)}
                  onChange={(_event, next: AdserverFormat | null) => {
                    if (!next) return;
                    changeNumber(
                      "adserverCost",
                      ADSERVER_COST_BY_FORMAT[next].toFixed(4)
                    );
                  }}
                  sx={{ flexWrap: "wrap" }}
                >
                  <ToggleButton value="VIDEO">
                    {t(
                      "briefingsCreate.form.hasVideoWithVideo",
                      "Rich media with video"
                    )}
                  </ToggleButton>
                  <ToggleButton value="RICH_MEDIA_NO_VIDEO">
                    {t(
                      "briefingsCreate.form.hasVideoWithoutVideo",
                      "Rich media without video"
                    )}
                  </ToggleButton>
                  <ToggleButton value="BANNER">
                    {t(
                      "briefingsCreate.form.hasVideoBanner",
                      "Banner with AdServer"
                    )}
                  </ToggleButton>
                </ToggleButtonGroup>
              </>
            )}
            <InlineNumber
              value={draft.adserverCost}
              onChange={(event) =>
                changeNumber("adserverCost", event.target.value)
              }
              error={error}
              autoFocus={autoFocus}
            />
          </Stack>
        );
      case "adtechCost":
        return (
          <Stack spacing={0.75}>
            <InlineNumber
              value={draft.adtechCost}
              onChange={(event) =>
                changeNumber("adtechCost", event.target.value)
              }
              error={error}
              autoFocus={autoFocus}
            />
            {adtechPercentage && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ flex: 1 }}
                >
                  {adtechManual
                    ? t("briefingsView.goalForm.autoAdtechManualHelper", {
                        percentage: Math.round(adtechPercentage * 100),
                      })
                    : t("briefingsView.goalForm.autoAdtechHelper", {
                        percentage: Math.round(adtechPercentage * 100),
                      })}
                </Typography>
                {adtechManual && (
                  <Button
                    size="small"
                    onClick={() => {
                      setAdtechManual(false);
                      setAdserverManual(false);
                    }}
                  >
                    {t(
                      "briefingsView.goalForm.reapplyAdtech",
                      "Reapply automatic value"
                    )}
                  </Button>
                )}
              </Stack>
            )}
          </Stack>
        );
      default:
        return (
          <InlineNumber
            value={draft[key]}
            onChange={(event) => changeNumber(key, event.target.value)}
            error={error}
            autoFocus={autoFocus}
          />
        );
    }
  };

  const NAME_SOURCES: GoalKey[] = ["country", "funnel", "objective"];

  const renderField = (key: GoalKey, span: number | "full" = 1) => {
    if (!canView(key)) return null;
    const value = draft[key];
    const editable = canEditField(key);
    const revert = NAME_SOURCES.includes(key)
      ? revertNameSource(key)
      : undefined;
    const isWide =
      key === "adserverCost" && isBeApoloCompany && section.isEditing(key);
    return (
      <InlineField
        key={key}
        span={isWide ? 2 : span}
        label={LABELS[key]}
        icon={ICONS[key]}
        auto={Boolean(AUTO_FIELDS[key])}
        autoHint={AUTO_FIELDS[key]}
        empty={!value}
        value={formatValue(key, value)}
        copyValue={copyValueOf(key, value)}
        input={editable ? renderInput(key) : undefined}
        {...bindField(section, key, editable, revert)}
      />
    );
  };

  // Warnings only while the related values are being edited.
  const warnings: string[] = [];
  const watchWarnings =
    creating ||
    (["startDate", "endDate", "expectedVolume"] as GoalKey[]).some((key) =>
      section.isDirty(key)
    );
  if (watchWarnings) {
    const start = draft.startDate ? dayjs(draft.startDate) : null;
    const end = draft.endDate ? dayjs(draft.endDate) : null;
    const today = dayjs().startOf("day");
    if (start && end && start.isSame(end, "day")) {
      warnings.push(
        t(
          "briefingsView.goalWarnings.sameDates",
          "¡Cuidado la fecha de inicio y final es la misma!"
        )
      );
    }
    if (
      (start && start.isBefore(today, "day")) ||
      (end && end.isBefore(today, "day"))
    ) {
      warnings.push(
        t(
          "briefingsView.goalWarnings.pastDates",
          "¡Cuidado la fecha de inicio o fin es anterior al día de hoy!"
        )
      );
    }
    if (Number(draft.expectedVolume) > 1000) {
      warnings.push(
        t(
          "briefingsView.goalWarnings.highVolume",
          "¡Cuidado el volumen previsto es muy alto!"
        )
      );
    }
    if (start && end && end.isBefore(start, "day")) {
      warnings.push(
        t(
          "briefingsView.goalWarnings.endBeforeStart",
          "¡Cuidado! La fecha de fin es anterior a la fecha de inicio."
        )
      );
    }
  }

  const hasGoal = Boolean(goal) || creating;

  return (
    <>
      <SectionCard
        icon={<FlagOutlinedIcon />}
        title={t("briefingsView.goalSectionTitle", "Goal")}
        subtitle={
          isCalculating ? (
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <CircularProgress size={10} color="info" />
              <span>{t("common.calculating", "Calculating...")}</span>
            </Stack>
          ) : (
            t(
              "briefingsView.goalSectionSubtitle",
              "Objetivo, inversión y desglose de costes"
            )
          )
        }
        dirtyCount={section.dirtyCount}
        saving={section.isSaving}
        saveDisabled={isCalculating}
        saveLabel={
          creating ? t("briefingsView.createGoal", "Create goal") : undefined
        }
        onSave={() => void section.save()}
        onDiscard={discard}
        createdAt={creating ? null : goal?.createdAt}
        updatedAt={creating ? null : goal?.updatedAt}
        actions={
          goal && onDelete ? (
            <Tooltip title={t("briefingsView.deleteGoal", "Delete goal")}>
              <IconButton
                size="small"
                onClick={onDelete}
                sx={{
                  color: "text.secondary",
                  "&:hover": { color: "error.main" },
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : undefined
        }
      >
        {!hasGoal ? (
          <SectionEmpty
            message={t(
              "briefingsView.goalNotFound",
              "This associated campaign does not have a goal yet."
            )}
            actionLabel={
              canEdit ? t("briefingsView.createGoal", "Create goal") : undefined
            }
            onAction={() => setCreating(true)}
          />
        ) : (
          <Stack spacing={3}>
            {warnings.length > 0 && (
              <Alert severity="warning" variant="outlined">
                <Stack spacing={0.25}>
                  {warnings.map((warning) => (
                    <span key={warning}>{warning}</span>
                  ))}
                </Stack>
              </Alert>
            )}

            <FieldGroup
              title={t(
                "briefingsView.goalSections.general",
                "General information"
              )}
              columns={4}
            >
              {renderField("startDate")}
              {renderField("endDate")}
              {renderField("objective")}
              {renderField("funnel")}
              {renderField("country")}
              {renderField("campaign", 3)}
              {renderField("campaignConcept", 2)}
              {renderField("orderName", 2)}
              {renderField("lineName", "full")}
            </FieldGroup>

            <FieldGroup
              title={t(
                "briefingsView.goalSections.performance",
                "Performance metrics"
              )}
              columns={4}
            >
              {renderField("margin")}
              {renderField("visibility")}
              {renderField("ctr")}
              {renderField("expectedVolume")}
              {renderField("netUnitCost")}
            </FieldGroup>

            <FieldGroup
              title={t("briefingsView.goalSections.costs", "Cost breakdown")}
              columns={4}
            >
              {renderField("grossUnitCost")}
              {renderField("totalInversion")}
              {renderField("platformInversion")}
              {renderField("adtechCost")}
              {renderField("adserverCost")}
              {renderField("zeotapDataCost")}
              {renderField("impressionTrackerCost")}
              {renderField("clickTrackerCost")}
            </FieldGroup>

            <FieldGroup
              title={t(
                "briefingsView.goalSections.additional",
                "Additional information"
              )}
              columns={4}
            >
              {renderField("currency")}
              {renderField("status")}
              {renderField("notes", 2)}
            </FieldGroup>
          </Stack>
        )}
      </SectionCard>

      <ConfirmationModal
        open={Boolean(budgetConfirm)}
        title={t(
          "briefingsView.budgetWarning.title",
          "¿Superar el presupuesto del briefing?"
        )}
        message={t(
          "briefingsView.budgetWarning.message",
          "La inversión total de este goal supera el presupuesto total del briefing. ¿Quieres guardar igualmente?"
        )}
        confirmLabel={t(
          "briefingsView.budgetWarning.confirm",
          "Guardar igualmente"
        )}
        onConfirm={() => {
          budgetConfirm?.(true);
          setBudgetConfirm(null);
        }}
        onCancel={() => {
          budgetConfirm?.(false);
          setBudgetConfirm(null);
        }}
      />
    </>
  );
};

export default AssociatedCampaignGoalSection;
